// DefectChatModal: the group-discussion thread for one asset defect.
//
// Pinned header (defect summary + status + actions) / scrollable message list /
// pinned composer footer, per the standing form-in-modal convention. While open
// it polls the thread every 5 seconds with an afterId cursor (Phase 2 swaps the
// poll for a socket push; the REST layer stays the source of truth either way)
// and advances the caller's read marker so unread counts stay honest.
//
// Completed defects lock the thread: the composer is replaced with a notice and
// a Report New Defect shortcut; inspectors get a Reopen action in the header.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { baseUrl } from '../../shared';
import { StatusChip } from '../ui';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import { getDefectSocket } from './defectSocket';

const POLL_MS = 5000;

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    username: localStorage.getItem('username'),
    bToken: localStorage.getItem('bToken'),
  };
}

function DaySeparator({ label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
      <Box sx={{ flex: 1, borderBottom: '1px solid var(--border)' }} />
      <Typography variant="caption" sx={{ color: 'var(--muted)', flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ flex: 1, borderBottom: '1px solid var(--border)' }} />
    </Box>
  );
}

// Image attachment inside a bubble. Fetched through the authenticated proxy
// (GET /api/attachment/:uploadId) as a blob: the raw FTP URL is http:// and
// would be blocked as mixed content on the https app. Click opens full size.
function AttachmentImage({ attachmentId }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    fetch(baseUrl + 'attachment/' + attachmentId, {
      headers: {
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('fetch failed'))))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachmentId]);

  if (failed) {
    return <Typography variant="caption" sx={{ color: 'var(--muted)', fontStyle: 'italic' }}>Attachment unavailable</Typography>;
  }
  if (!src) {
    return <Box sx={{ width: 180, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', borderRadius: '8px' }}><CircularProgress size={20} /></Box>;
  }
  return (
    <Box
      component="img"
      src={src}
      alt="Defect attachment"
      onClick={() => window.open(src, '_blank', 'noopener')}
      sx={{ maxWidth: '100%', maxHeight: 220, borderRadius: '8px', cursor: 'pointer', display: 'block' }}
    />
  );
}

function MessageBubble({ msg, mine }) {
  if (msg.messageType === 'SYSTEM') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'var(--muted)', textAlign: 'center', fontStyle: 'italic', px: 2 }}>
          {msg.messageText} · {moment(msg.createdAt).format('DD-MM-YYYY hh:mm A')}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', my: 0.5 }}>
      <Box sx={{ maxWidth: '78%' }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: mine ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
            background: mine ? 'var(--primary-tint)' : 'var(--surface-2)',
            border: '1px solid var(--border)',
          }}
        >
          {!mine && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--primary)', display: 'block' }}>
              {msg.authorName || msg.authorUsername}
              {msg.authorCompany ? <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {msg.authorCompany}</span> : null}
            </Typography>
          )}
          {msg.attachmentId ? (
            <Box sx={{ mt: !mine ? 0.5 : 0 }}>
              <AttachmentImage attachmentId={msg.attachmentId} />
            </Box>
          ) : null}
          {msg.messageText ? (
            <Typography variant="body2" sx={{ color: 'var(--text)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: msg.attachmentId ? 0.5 : 0 }}>
              {msg.messageText}
            </Typography>
          ) : null}
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', textAlign: mine ? 'right' : 'left', mt: 0.25 }}>
          {moment(msg.createdAt).format('hh:mm A')}
        </Typography>
      </Box>
    </Box>
  );
}

export default function DefectChatModal({ open, defectId, onClose, onMarkComplete, onReportNew, onChanged, onOpenAsset }) {
  const online = useOnlineStatus();
  const [defect, setDefect] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [reopenConfirm, setReopenConfirm] = useState(false);
  const [reopening, setReopening] = useState(false);
  const listRef = useRef(null);
  const lastIdRef = useRef(0);
  const stickToBottomRef = useRef(true);

  const myUsername = localStorage.getItem('username');
  const canInspect = localStorage.getItem('canInspectAsset') === '1';
  const canAddDefect = localStorage.getItem('canAddDefect') === '1';
  const completed = !!(defect && defect.resolvedAt);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const markRead = useCallback((lastId) => {
    if (!lastId) return;
    fetch(baseUrl + 'defect/' + defectId + '/read', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ lastReadMessageId: lastId }),
    }).catch(() => {});
  }, [defectId]);

  const fetchMessages = useCallback(async (initial) => {
    const afterId = initial ? 0 : lastIdRef.current;
    const res = await fetch(baseUrl + 'defect/' + defectId + '/messages?afterId=' + afterId, {
      headers: authHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Could not load the discussion');
    }
    const data = await res.json();
    setDefect(data.defect);
    if (data.messages.length > 0) {
      lastIdRef.current = data.messages[data.messages.length - 1].id;
      // Dedupe on append: an overlapping poll/focus/send can fetch the same
      // afterId window twice, and the send response may race the next poll.
      setMessages((prev) => {
        if (initial) return data.messages;
        const seen = new Set(prev.map((m) => m.id));
        const fresh = data.messages.filter((m) => !seen.has(m.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      markRead(lastIdRef.current);
    } else if (initial) {
      setMessages([]);
    }
    return data.messages.length;
  }, [defectId, markRead]);

  // Load on open, then live-push via the shared socket (room defect:<id>) with
  // the 5s afterId poll kept as the always-on fallback; also refresh on window
  // focus so a returning tab catches up immediately instead of on the next tick.
  useEffect(() => {
    if (!open || !defectId) return undefined;
    let cancelled = false;
    lastIdRef.current = 0;
    stickToBottomRef.current = true;
    setMessages([]);
    setDefect(null);
    setLoadError('');
    setSendError('');
    setDraft('');
    setLoading(true);
    fetchMessages(true)
      .catch((err) => { if (!cancelled) setLoadError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    const poll = () => { fetchMessages(false).catch(() => {}); };
    const interval = setInterval(poll, POLL_MS);
    window.addEventListener('focus', poll);

    const socket = getDefectSocket();
    const onSocketMessage = (msg) => {
      if (!msg || msg.defectId !== defectId) return;
      // SYSTEM messages mean the defect state may have changed (completed /
      // reopened), so refetch: the poll response carries the defect summary.
      if (msg.messageType === 'SYSTEM') { poll(); return; }
      if (msg.id > lastIdRef.current) lastIdRef.current = msg.id;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      markRead(msg.id);
    };
    // Rooms don't survive a reconnect: rejoin and catch up on every connect.
    const rejoin = () => { socket.emit('defect:join', defectId); poll(); };
    if (socket) {
      socket.emit('defect:join', defectId);
      socket.on('defect:message', onSocketMessage);
      socket.on('connect', rejoin);
    }

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', poll);
      if (socket) {
        socket.emit('defect:leave', defectId);
        socket.off('defect:message', onSocketMessage);
        socket.off('connect', rejoin);
      }
    };
  }, [open, defectId, fetchMessages, markRead]);

  // Follow new messages only while the reader is already near the bottom.
  useEffect(() => {
    if (stickToBottomRef.current) scrollToBottom();
  }, [messages]);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  async function sendMessage() {
    const messageText = draft.trim();
    if (!messageText || sending) return;
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(baseUrl + 'defect/' + defectId + '/messages', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ messageText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Message could not be sent');
      setDraft('');
      stickToBottomRef.current = true;
      if (data.message) {
        if (data.message.id > lastIdRef.current) lastIdRef.current = data.message.id;
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      }
      if (onChanged) onChanged();
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  }

  // Attach a photo: uploads immediately on file pick, taking any typed draft
  // text along as the caption (mirrors messaging-app behaviour).
  async function uploadAttachment(file) {
    if (!file || sending) return;
    setSending(true);
    setSendError('');
    try {
      const formData = new FormData();
      formData.append('myFile', file);
      const caption = draft.trim();
      if (caption) formData.append('caption', caption);
      const res = await fetch(baseUrl + 'defect/' + defectId + '/attachment', {
        method: 'POST',
        headers: {
          username: localStorage.getItem('username'),
          bToken: localStorage.getItem('bToken'),
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Photo could not be uploaded');
      setDraft('');
      stickToBottomRef.current = true;
      if (data.message) {
        if (data.message.id > lastIdRef.current) lastIdRef.current = data.message.id;
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      }
      if (onChanged) onChanged();
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  }

  async function reopenDefect() {
    setReopening(true);
    try {
      const res = await fetch(baseUrl + 'defect/' + defectId + '/reopen', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Defect could not be reopened');
      setReopenConfirm(false);
      lastIdRef.current = 0;
      await fetchMessages(true).catch(() => {});
      if (onChanged) onChanged();
    } catch (err) {
      setSendError(err.message);
      setReopenConfirm(false);
    } finally {
      setReopening(false);
    }
  }

  // Group messages by calendar day for the separators.
  const grouped = [];
  let lastDay = null;
  messages.forEach((msg) => {
    const day = moment(msg.createdAt).format('DD-MM-YYYY');
    if (day !== lastDay) {
      grouped.push({ separator: true, key: 'day-' + day, label: day });
      lastDay = day;
    }
    grouped.push({ separator: false, key: 'msg-' + msg.id, msg });
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh', minHeight: '55vh' }}>

          {/* Pinned header */}
          <Box sx={{ flexShrink: 0, px: 3, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Defect Discussion</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {defect && !completed && canInspect && (
                  <Tooltip title="Mark defect as Complete">
                    <IconButton onClick={() => onMarkComplete && onMarkComplete(defect.id)} sx={{ color: 'var(--pass)' }}>
                      <CheckCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {defect && completed && canInspect && (
                  <Tooltip title="Reopen defect">
                    <IconButton onClick={() => setReopenConfirm(true)} sx={{ color: 'var(--warn)' }}>
                      <ReplayIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
              </Box>
            </Box>
            {defect && (
              <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <StatusChip domain="defect" value={completed ? 'COMPLETED' : 'OPEN'} />
                  {defect.priority ? <StatusChip domain="defect" value={defect.priority} /> : null}
                  {onOpenAsset ? (
                    <Typography
                      variant="body2"
                      component="button"
                      onClick={() => onOpenAsset(defect)}
                      className="aims-mono"
                      sx={{ color: 'var(--primary)', background: 'none', border: 'none', p: 0, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {defect.assetId}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'var(--muted)' }} className="aims-mono">
                      {defect.assetId}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--text)', mt: 0.5, overflowWrap: 'anywhere' }}>
                  {defect.defectComment}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
                  Reported {moment(defect.createdAt).format('DD-MM-YYYY hh:mm A')}
                  {defect.reportedBy ? ' by ' + defect.reportedBy : ''} (User: {defect.addedBy})
                </Typography>
              </Box>
            )}
          </Box>

          {/* Scrollable message list */}
          <Box ref={listRef} onScroll={handleListScroll} sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 1.5, background: 'var(--surface)' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            )}
            {!loading && loadError && (
              <Typography variant="body2" sx={{ color: 'var(--fail)', textAlign: 'center', py: 3 }}>{loadError}</Typography>
            )}
            {!loading && !loadError && messages.length === 0 && (
              <Typography variant="body2" sx={{ color: 'var(--muted)', textAlign: 'center', py: 3 }}>
                No messages yet. Start the discussion below.
              </Typography>
            )}
            {grouped.map((item) =>
              item.separator
                ? <DaySeparator key={item.key} label={item.label} />
                : <MessageBubble key={item.key} msg={item.msg} mine={item.msg.authorUsername === myUsername} />
            )}
          </Box>

          {/* Pinned footer: composer, or the locked-thread notice when completed */}
          <Box sx={{ flexShrink: 0, px: 2.5, py: 1.5, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
            {sendError && (
              <Typography variant="caption" sx={{ color: 'var(--fail)', display: 'block', mb: 0.5 }}>{sendError}</Typography>
            )}
            {completed ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: 'var(--info-bg)', borderRadius: 'var(--r)', px: 1.5, py: 1 }}>
                <InfoOutlinedIcon sx={{ color: 'var(--info)', flexShrink: 0 }} fontSize="small" />
                <Typography variant="caption" sx={{ color: 'var(--text)', flex: 1 }}>
                  This defect is completed. If there is still an issue, report a new defect.
                </Typography>
                {canAddDefect && onReportNew && (
                  <Button size="small" variant="outlined" startIcon={<ReportProblemIcon />} onClick={onReportNew} sx={{ flexShrink: 0 }}>
                    Report New Defect
                  </Button>
                )}
              </Box>
            ) : !online ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: 'var(--info-bg)', borderRadius: 'var(--r)', px: 1.5, py: 1 }}>
                <InfoOutlinedIcon sx={{ color: 'var(--info)', flexShrink: 0 }} fontSize="small" />
                <Typography variant="caption" sx={{ color: 'var(--text)' }}>
                  You are offline. Reconnect to send messages.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <Tooltip title="Attach photo">
                  <span>
                    <IconButton component="label" disabled={sending || !defect} sx={{ color: 'var(--muted)' }}>
                      <ImageIcon />
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          e.target.value = '';
                          if (file) uploadAttachment(file);
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder="Write a message"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={sending || !defect}
                />
                <Tooltip title="Send">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={sendMessage}
                      disabled={sending || !draft.trim() || !defect}
                    >
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Reopen confirmation */}
      <Dialog open={reopenConfirm} onClose={() => setReopenConfirm(false)}>
        <DialogTitle>Reopen this defect?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The defect will return to the open list and the discussion will unlock. The original resolution stays recorded in the thread.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
          <Button onClick={() => setReopenConfirm(false)} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }}>Cancel</Button>
          <Button onClick={reopenDefect} variant="contained" color="warning" disabled={reopening}>
            {reopening ? 'Reopening...' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
